"use client";

import type {
  ForumPostState,
  ForumVote,
  ForumComposeComment,
  ForumCommentDeletion,
  ForumImageShare,
  ForumMarkdownRenderer,
  ForumCommentVoteHandler,
} from "../forum.types";
import type { ForumLayoutProps } from "../forum-layout";
import { ForumPostContent } from "./forum-post-content";
import { useRef } from "react";
import * as stylex from "@stylexjs/stylex";
import { styles } from "../forum.styles";

import { ForumLayout } from "../forum-layout";
import { RefreshFeedback } from "../forum-refresh-feedback";
import { Button, Spinner } from "@softmax/design-system";

export type ForumPostViewProps = ForumLayoutProps &
  Readonly<{
    backHref: string;
    state: ForumPostState;
    postVote?: ForumVote | undefined;
    commentVotes?: Readonly<Record<string, ForumVote>>;
    onRefresh(): void;
    onPostVote(direction: 1 | -1): void;
    onCommentVote: ForumCommentVoteHandler;
    renderMarkdown: ForumMarkdownRenderer;
    compose?: ForumComposeComment | undefined;
    deleteComment?: ForumCommentDeletion | undefined;
    shareImage?: ForumImageShare | undefined;
  }>;

export function ForumPostView(props: ForumPostViewProps) {
  const {
    layout = "page",
    description,
    homeHref,
    wikiRail,
    name,
    backHref,
    state,
    postVote,
    commentVotes,
    onRefresh,
    onPostVote,
    onCommentVote,
    renderMarkdown,
    renderLink,
    compose,
    deleteComment,
    shareImage,
  } = props;
  const refreshTrigger = useRef<HTMLButtonElement>(null);
  const retry = () => {
    refreshTrigger.current?.focus();
    onRefresh();
  };
  const Container = layout === "page" ? "main" : "section";
  return (
    <Container
      id={layout === "page" ? "main" : undefined}
      {...stylex.props(layout === "page" ? styles.page : styles.embedded)}
    >
      <ForumLayout
        description={description}
        homeHref={homeHref}
        wikiRail={wikiRail}
        renderLink={renderLink}
      >
        <div {...stylex.props(styles.toolbar)}>
          {renderLink({
            href: backHref,
            ...stylex.props(styles.link),
            children: "← Forum",
          })}
          {layout === "page" && (
            <Button
              ref={refreshTrigger}
              aria-disabled={state.status === "ready" && state.refreshing}
              onClick={() => {
                if (state.status !== "ready" || !state.refreshing) onRefresh();
              }}
            >
              Refresh
            </Button>
          )}
        </div>
        {state.status === "loading" && (
          <output {...stylex.props(styles.loading)}>
            <Spinner />
            Loading the post…
          </output>
        )}
        {state.status === "error" && (
          <RefreshFeedback
            refreshing={false}
            error={state.message}
            onRefresh={retry}
          />
        )}
        {state.status === "ready" && (
          <ForumPostContent
            state={state}
            layout={layout}
            postVote={postVote}
            onPostVote={onPostVote}
            shareImage={shareImage}
            name={name}
            renderLink={renderLink}
            renderMarkdown={renderMarkdown}
            commentVotes={commentVotes}
            onCommentVote={onCommentVote}
            deleteComment={deleteComment}
            compose={compose}
            onRefresh={retry}
          />
        )}
      </ForumLayout>
    </Container>
  );
}
